// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { Outlet, RouteObject } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import { MainPanel } from "types";

import UserAdmin from "view/page/UserAdmin";
import UserInfo from "view/page/UserInfo";
import SiteSelection from "view/page/SiteSelection";
import SiteAdmin from "view/page/SiteAdmin";
import SiteParameters from "view/page/SiteParameters";
import Dashboard from "view/page/Dashboard";
import IoList from "view/page/IoList";
import IoTrend from "view/page/IoTrend";
import PowerPage from "view/page/PowerPage";
import EnergyMeterList from "view/comp/EnergyMeterList";
import Alarm from "view/page/Alarm";
import Monitor from "view/page/Monitor";
import Trend from "view/page/Trend";
import TrendMonitor from "view/page/TrendMonitor";
import ChargerPage from "view/page/ChargerPage";
import RfidPage from "view/page/RfidPage";
import EnlineChargers from "view/page/EnlineChargers";
import OcppConfiguration from "view/page/OcppConfiguration";
import ChargingSession from "view/page/ChargingSession";
import ChargingSessionPage from "view/page/ChargingSessionPage";
import Reports from "view/page/Reports";
import LocalSettings from "view/page/LocalSettings";
import ReportDataView from "view/page/ReportDataView";
import ModulesConditions from "view/page/ModulesConditions";
import ConditionDetailPage from "view/page/ConditionDetailPage";
import CustomDashboard from "view/page/CustomDashboard";

import IconDashboard from "icons/navDashboard";
import IconMonitor from "icons/navMonitor";
import IconPower from "icons/navPower";
import IconTrend from "icons/navTrend";
import IconDisplay from "icons/navDisplay";
import IconModules from "icons/navModules";
import IconIOGraph from "icons/navIoGraph";
import IconSwitching from "icons/navSwitching";
import IconCharger from "icons/navCharger";
import IconRfid from "icons/navRfid";
import IconChargerSessionList from "icons/navChargerSessionList";
import IconChargingSession from "icons/navChargingSession";
import IconOcppSettings from "icons/navOcppSettings";

// uses webpack definitions
declare const IS_CLOUD: boolean;

type INavElement = {
  icon: React.ReactElement;
  mainIcon?: React.ReactElement;
  label: React.ReactElement;
  mainLabel?: React.ReactElement;
};

type NavMap = {
  [panel in MainPanel]?: INavElement;
};

export const childRoutes: RouteObject[] = [
  { index: true, element: <Dashboard /> },
  { path: MainPanel.DASHBOARD, element: <Dashboard /> },
  { path: MainPanel.MONITOR, element: <Monitor /> },
  { path: MainPanel.USER, element: <UserInfo /> },
  { path: MainPanel.USER_SETTINGS, element: <UserAdmin /> },
  { path: MainPanel.SITES, element: <SiteSelection /> },
  // same instance as SITES, keeps the map mounted when switching
  { path: MainPanel.SITE_INFO, element: <SiteSelection showConnectedSite /> },
  { path: MainPanel.SITE_SETTINGS, element: <SiteAdmin /> },
  { path: MainPanel.SITE_PARAMETERS, element: <SiteParameters /> },
  { path: MainPanel.POWER, element: <PowerPage /> },
  { path: MainPanel.ENERGYMETERS, element: <EnergyMeterList /> },
  { path: MainPanel.ENERGY, element: <Trend /> },
  { path: MainPanel.TRENDMONITOR, element: <TrendMonitor /> },
  { path: MainPanel.REPORT, element: <Reports /> },
  { path: MainPanel.REPORTDATAVIEW, element: <ReportDataView /> },
  { path: MainPanel.IOS, element: <IoList /> },
  { path: MainPanel.IO_TREND, element: <IoTrend /> },
  { path: MainPanel.CHARGERLIST, element: <ChargerPage /> },
  { path: MainPanel.CHARGERDETAIL, element: <ChargerPage /> },
  { path: MainPanel.RFIDLIST, element: <RfidPage /> },
  { path: MainPanel.RFIDDETAIL, element: <RfidPage /> },
  { path: MainPanel.ENLINE_CHARGER, element: <EnlineChargers /> },
  { path: MainPanel.OCPPCONFIGURATION, element: <OcppConfiguration /> },
  { path: MainPanel.CHARGINGSESSIONLIST, element: <ChargingSessionPage /> },
  { path: MainPanel.CHARGINGSESSION, element: <ChargingSession /> },
  { path: MainPanel.ALARMS, element: <Alarm /> },
  { path: MainPanel.MODULES, element: <ModulesConditions /> },
  { path: MainPanel.CONDITIONDETAIL, element: <ConditionDetailPage /> },
  { path: MainPanel.LOCAL_SETTINGS, element: <LocalSettings /> },
  { path: MainPanel.CUSTOM_DASHBOARD, element: <CustomDashboard isTopLevel={true} /> },
  { path: MainPanel.PUBLIC_DISPLAY, element: <CustomDashboard isTopLevel={false} /> },
];

// setup list of shown panels
export const navEntries: NavMap = {
  [MainPanel.DASHBOARD]: {
    icon: <IconDashboard />,
    label: <FormattedMessage id="Dashboard" />,
  },
  [MainPanel.MONITOR]: {
    icon: <IconMonitor />,
    label: <FormattedMessage id="Monitor" />,
  },
  [MainPanel.POWER]: {
    icon: <IconPower />,
    label: <FormattedMessage id="Power" />,
  },
  [MainPanel.ENERGY]: {
    icon: <IconTrend />,
    label: <FormattedMessage id="nav_Trend Data" defaultMessage="Trend Data" />,
  },
  [MainPanel.TRENDMONITOR]: {
    icon: <IconMonitor />,
    label: <FormattedMessage id="Trend Monitor" />,
  },
  [MainPanel.PUBLIC_DISPLAY]: {
    icon: <IconDisplay />,
    label: <FormattedMessage id="Public Display" />,
  },
  [MainPanel.IOS]: {
    icon: <IconSwitching />,
    label: <FormattedMessage id="Current Status" />,
    mainLabel: <FormattedMessage id="Input/Outputs" />,
  },
  [MainPanel.IO_TREND]: {
    icon: <IconIOGraph />,
    label: <FormattedMessage id="IO Graph" />,
  },
  [MainPanel.MODULES]: {
    icon: <IconModules />,
    label: <FormattedMessage id="Modules/Conditions" />,
  },
  [MainPanel.CHARGERLIST]: {
    icon: <IconCharger />,
    label: <FormattedMessage id="Chargers" />,
    mainLabel: <FormattedMessage id="E-Mobility" />,
  },
  [MainPanel.ENLINE_CHARGER]: {
    icon: <IconCharger />,
    label:
      IS_CLOUD ?
        <FormattedMessage id="Energy Management Chargers" />
      : <FormattedMessage id="Chargers" />,
  },
  [MainPanel.OCPPCONFIGURATION]: {
    icon: <IconOcppSettings />,
    label: <FormattedMessage id="Configuration" />,
  },
  [MainPanel.CHARGINGSESSIONLIST]: {
    icon: <IconChargerSessionList />,
    label: <FormattedMessage id="Charging Sessions" />,
  },
  [MainPanel.CHARGINGSESSION]: {
    icon: <IconChargingSession />,
    label: <FormattedMessage id="Active Session" />,
  },
  [MainPanel.RFIDLIST]: {
    icon: <IconRfid />,
    label: <FormattedMessage id="Charging Cards" />,
  },
};

export default function ContentPanel() {
  return <Outlet />;
}
